import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatApiError } from '../lib/utils';
import { Headphones, ArrowLeft, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
    const [searchParams] = useSearchParams();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('operator');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [honeypot, setHoneypot] = useState('');
    const [formLoadTime] = useState(Date.now());
    const { register } = useAuth();
    const brand = useBrand();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (honeypot) return; // Bot filled the hidden field
        if (Date.now() - formLoadTime < 3000) { toast.error('Please take a moment before submitting'); return; }
        setError('');
        setLoading(true);

        try {
            const user = await register(email, password, name, role);
            toast.success('Account created successfully!');
            if (user.role === 'operator') {
                navigate('/operator');
            } else {
                navigate('/my-guides');
            }
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0D14] flex">
            {/* Left Panel - Image */}
            <div className="hidden lg:block lg:w-1/2 relative">
                <img 
                    src="https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1200"
                    alt="Rome Colosseum"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-[#0A0D14] via-transparent to-transparent" />
            </div>

            {/* Right Panel - Form */}
            <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16">
                <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white mb-12 transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                    Back to home
                </Link>

                <div className="max-w-md">
                    <div className="flex items-center gap-3 mb-8">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="w-10 h-10 rounded-md object-contain" />
                        ) : (
                            <div className="w-10 h-10 rounded-md bg-gold flex items-center justify-center">
                                <Headphones className="w-5 h-5 text-[#0A0D14]" />
                            </div>
                        )}
                        <span className="font-outfit font-semibold text-xl text-white">{brand.platform_name}</span>
                    </div>

                    <h1 className="font-outfit text-3xl font-bold text-white mb-2">Create your operator account</h1>
                    <p className="text-muted-foreground mb-8">Start managing audio guides for your business</p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Honeypot */}
                        <div className="absolute opacity-0 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                            <input type="text" name="company_url" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
                        </div>
                        {error && (
                            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="name">{role === 'operator' ? 'Company Name' : 'Full Name'}</Label>
                            <Input
                                id="name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={role === 'operator' ? 'Your Company Name' : 'John Doe'}
                                required
                                className="bg-[#121620] border-white/10 focus:border-gold focus:ring-gold"
                                data-testid="register-name-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="bg-[#121620] border-white/10 focus:border-gold focus:ring-gold"
                                data-testid="register-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                minLength={6}
                                className="bg-[#121620] border-white/10 focus:border-gold focus:ring-gold"
                                data-testid="register-password-input"
                            />
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full bg-gold text-[#0A0D14] hover:bg-gold/90"
                            disabled={loading}
                            data-testid="register-submit-btn"
                        >
                            {loading ? 'Creating account...' : 'Create Account'}
                        </Button>
                    </form>

                    <p className="mt-6 text-center text-muted-foreground text-sm">
                        Already have an account?{' '}
                        <Link to="/login" className="text-gold hover:underline">Sign in</Link>
                    </p>
                    <p className="mt-2 text-center text-muted-foreground text-sm">
                        Traveler?{' '}
                        <Link to="/access" className="text-gold hover:underline">Enter your voucher code</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
